import styled, { keyframes } from 'styled-components';

import type { AutosaveStatus } from '../../hooks/useAutosave';

type Props = {
  status: AutosaveStatus;
  /**
   * Kept in the prop signature for compatibility with callers that still
   * pass it (AutosaveStatusIcon uses the timestamp). The indicator itself
   * intentionally ignores it — this chip is two-state only.
   */
  lastSavedAt?: Date | null;
};

/**
 * Ambient autosave status chip — minimalist two-state variant.
 *
 *   saving → "Saving…"      (spinner)
 *   saved  → "Saved"
 *
 * No timestamps, no error copy, no idle string. Error states are surfaced
 * through the paired `AutosaveStatusIcon` popover, not this text chip.
 */
export function AutosaveStatusIndicator({ status }: Props) {
  if (status !== 'saving' && status !== 'saved') return null;

  return (
    <Wrapper data-testid="autosave-status-indicator" aria-live="polite">
      {status === 'saving' && <Spinner />}
      <Label>{status === 'saving' ? 'Saving…' : 'Saved'}</Label>
    </Wrapper>
  );
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  user-select: none;
  white-space: nowrap;
`;

const Label = styled.span`
  line-height: 1.4;
`;

const Spinner = styled.span`
  width: 10px;
  height: 10px;
  border: 1.5px solid var(--border);
  border-top-color: var(--text-muted);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;
